"use client";

import { useTranslation } from "@/lib/i18n/context";

/**
 * Deep-dive section for the /product/goals page. Renders goals the way the app
 * itself does — plain prose sentences in italic Fraunces, grouped by cycle,
 * an expanded goal detail, and a deadline → daily-plan suggestion — using
 * illustrative examples (a medtech founder's goals). Real component output, not
 * a static mockup.
 */
type GoalEx = { title: string; due: string };

const YEARLY: GoalEx[] = [
  { title: "Close Series A — $8M at $40M post", due: "DEC 31" },
  { title: "Reach 50 paying hospital systems", due: "DEC 31" },
  { title: "Hire VP Engineering & Head of Regulatory", due: "DEC 31" },
];
const MONTHLY: GoalEx[] = [
  { title: "Ship CardioMonitor 2.1 to all customers", due: "NOV 30" },
  { title: "Board prep — November meeting", due: "NOV 30" },
  { title: "Onboard Mayo Clinic pilot to production", due: "NOV 30" },
];
const WEEKLY: GoalEx[] = [
  { title: "Close VP Engineering finalist round", due: "FRI" },
  { title: "FDA Q-Sub response sent", due: "FRI" },
  { title: "1:1 with every direct report", due: "FRI" },
];

function GoalColumn({ label, goals }: { label: string; goals: GoalEx[] }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-soft)]/80">
        {label}
      </div>
      <ul className="flex flex-col gap-3 m-0 p-0 list-none">
        {goals.map((g) => (
          <li key={g.title} className="rounded-xl border border-[var(--rule)] bg-[var(--paper)] px-4 py-3">
            <p className="m-0 font-display italic text-[15px] leading-[1.4] text-[var(--ink)]">{g.title}</p>
            <div className="mt-2 flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--terracotta)]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--ink-soft)]">{g.due}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function GoalsDeepDive() {
  const { lang } = useTranslation();
  const en = lang === "en";

  const labels = en
    ? { yearly: "Yearly", monthly: "Monthly", weekly: "This week" }
    : { yearly: "Letno", monthly: "Mesečno", weekly: "Ta teden" };

  return (
    <section className="relative max-w-[1120px] mx-auto px-6 md:px-12 py-16 md:py-24 z-[2]">
      <div className="max-w-[720px] mb-10 md:mb-14">
        <div className="kicker-mono inline-flex items-center gap-2.5 mb-5">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" />
          {en ? "// GOALS, ALIGNED" : "// CILJI, USKLAJENI"}
        </div>
        <h2 className="font-display text-[clamp(28px,3.4vw,44px)] leading-[1.08] tracking-[-0.02em] text-balance" style={{ fontWeight: 380 }}>
          {en ? (
            <>Goals you actually <em className="text-[var(--terracotta)] italic">write</em> — and a day that points at them.</>
          ) : (
            <>Cilji, ki jih res <em className="text-[var(--terracotta)] italic">zapišeš</em> — in dan, ki vanje meri.</>
          )}
        </h2>
        <p className="mt-4 text-[16px] md:text-[18px] leading-[1.6] text-[var(--ink-soft)] text-pretty">
          {en
            ? "Set a few directions for the year, the month, and the week. Link your tasks to them. Every morning your plan is built from the goals that matter most — not just the inbox."
            : "Postavi nekaj usmeritev za leto, mesec in teden. Poveži naloge nanje. Vsako jutro se tvoj plan sestavi iz ciljev, ki najbolj štejejo — ne le iz inboxa."}
        </p>
      </div>

      {/* The goals board — three cycles, prose-sentence goals, just like the app */}
      <div className="rounded-2xl border border-[var(--rule)] bg-[var(--paper-2)]/30 p-5 md:p-7">
        <div className="grid gap-6 md:grid-cols-3">
          <GoalColumn label={labels.yearly} goals={YEARLY} />
          <GoalColumn label={labels.monthly} goals={MONTHLY} />
          <GoalColumn label={labels.weekly} goals={WEEKLY} />
        </div>
      </div>

      {/* 1 — Goal detail (expanded), mirroring the in-app goal card */}
      <div className="mt-6 grid gap-5 lg:grid-cols-[1.35fr_1fr] items-start">
        <div className="rounded-2xl border border-[var(--rule)] bg-[var(--paper)] p-5 md:p-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--ink-soft)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--terracotta)]" /> Yearly
              <span className="opacity-40">·</span> DEC 31, 2026 · 187 DAYS
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--terracotta)]/[0.10] px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--terracotta)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--terracotta)]" /> Active
            </span>
          </div>
          <h3 className="mt-3 mb-4 font-display text-[24px] leading-[1.15] text-[var(--ink)]" style={{ fontWeight: 400 }}>
            Close Series A — $8M at $40M post
          </h3>
          <div className="grid gap-5 border-t border-[var(--rule)]/60 pt-4 sm:grid-cols-2">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--terracotta)]">{en ? "Description" : "Opis"}</p>
              <p className="mt-2 font-display italic text-[14.5px] leading-[1.5] text-[var(--ink)]">
                &ldquo;Lead investor signed, finalising syndicate. Target announcement Q1 next year. Diligence open with two firms.&rdquo;
              </p>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--ink-soft)]/70">{en ? "Created" : "Ustvarjeno"} · MAY 11, 2026</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--ink-soft)]/80">{en ? "Change timeline" : "Časovnica sprememb"}</p>
              <ul className="mt-2 m-0 p-0 list-none flex flex-col gap-2.5">
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full border border-[var(--ink-soft)]/50" />
                  <span className="text-[13px] text-[var(--ink-soft)]"><span className="font-mono text-[10px] tracking-[0.06em]">11 MAY · 17:14</span> — {en ? "Goal created" : "Cilj ustvarjen"}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--moss)]" />
                  <span className="text-[13px] text-[var(--ink-soft)]"><span className="font-mono text-[10px] tracking-[0.06em]">02 JUN · 09:20</span> — {en ? "Lead investor signed" : "Vodilni investitor podpisal"}</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-4 border-t border-[var(--rule)]/60 pt-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--terracotta)]">{en ? "Linked tasks" : "Povezane naloge"} · 2</p>
            <ul className="mt-2 m-0 p-0 list-none flex flex-col gap-1.5">
              {["Draft the Series A deck", "Prepare the diligence data room"].map((task) => (
                <li key={task} className="flex items-center gap-2 text-[13.5px] text-[var(--ink)]">
                  <span className="grid h-4 w-4 place-items-center rounded-[5px] border border-[var(--rule)] text-[var(--moss)]">✓</span>
                  {task}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className="font-display italic text-[15px] leading-[1.55] text-[var(--ink-soft)] lg:pt-4">
          {en
            ? "Open any goal to see its story — a living description, a timeline of every change, and the tasks pulling it forward. A goal isn't a checkbox; it's a thread you can follow."
            : "Odpri kateri koli cilj in vidiš njegovo zgodbo — živ opis, časovnico vsake spremembe in naloge, ki ga premikajo. Cilj ni kljukica; je nit, ki ji lahko slediš."}
        </p>
      </div>

      {/* 2 — Deadline approaching → the morning plan proposes a linked task */}
      <div className="mt-6 rounded-2xl border border-[var(--terracotta)]/30 bg-[var(--terracotta)]/[0.05] p-5 md:p-7">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--terracotta)]">
          {en ? "// WHEN A DEADLINE NEARS" : "// KO SE BLIŽA ROK"}
        </p>
        <div className="mt-4 grid gap-4 md:grid-cols-[1fr_auto_1.1fr] md:items-center">
          {/* The goal nearing its deadline */}
          <div className="rounded-xl border border-[var(--rule)] bg-[var(--paper)] px-4 py-3">
            <p className="m-0 font-display italic text-[15px] text-[var(--ink)]">FDA Q-Sub response sent</p>
            <div className="mt-2 flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--terracotta)] animate-pulse" />
              <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--terracotta)]">{en ? "Due in 6 days" : "Rok čez 6 dni"}</span>
            </div>
          </div>
          <div className="hidden md:flex items-center justify-center font-mono text-[18px] text-[var(--ink-soft)]">→</div>
          {/* The morning plan suggestion */}
          <div className="rounded-xl border border-[var(--rule)] bg-[var(--paper)] px-4 py-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--ink-soft)]/70">{en ? "Today's plan" : "Današnji plan"}</p>
            <div className="mt-2 flex items-start gap-2">
              <span className="text-[var(--terracotta)]">✦</span>
              <div>
                <p className="m-0 text-[14px] text-[var(--ink)]">{en ? "Suggested · Draft the Q-Sub cover letter" : "Predlog · Pripravi spremni dopis za Q-Sub"}</p>
                <p className="mt-1 inline-flex items-center gap-1.5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-soft)]/70">{en ? "linked to" : "povezano s"}</span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-[var(--terracotta)]/30 bg-[var(--terracotta)]/[0.06] px-2 py-0.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[var(--terracotta)]" />
                    <span className="font-display italic text-[12px] text-[var(--ink)]">FDA Q-Sub</span>
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
        <p className="mt-4 max-w-[760px] text-[14px] leading-[1.6] text-[var(--ink-soft)]">
          {en
            ? "When a goal's deadline comes within about a month, the morning plan automatically proposes a next task to move it forward — and links it back to the goal. Important things stop slipping because the day quietly reminds you of them."
            : "Ko je rok cilja oddaljen kakšen mesec, jutranji plan samodejno predlaga naslednjo nalogo, ki ga premakne naprej — in jo poveže nazaj na cilj. Pomembne stvari ne uhajajo več, ker te dan tiho spomni nanje."}
        </p>
      </div>

      {/* Two short explainers */}
      <div className="mt-8 grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-[var(--rule)] bg-[var(--paper)] p-5">
          <h3 className="m-0 font-display text-[18px] text-[var(--ink)]">{en ? "What you can write" : "Kaj lahko zapišeš"}</h3>
          <p className="mt-2 text-[14px] leading-[1.6] text-[var(--ink-soft)]">
            {en
              ? "A goal is a plain sentence — a direction or a target, with an optional deadline. Yearly intentions, a monthly push, this week's focus. Or just tell the AI assistant: \"add goal: 50 hospital systems by year end\"."
              : "Cilj je preprost stavek — usmeritev ali tarča, z neobveznim rokom. Letne namere, mesečni zalet, fokus tega tedna. Ali pa preprosto reci AI pomočniku: »dodaj cilj: 50 bolnišnic do konca leta«."}
          </p>
        </div>
        <div className="rounded-2xl border border-[var(--rule)] bg-[var(--paper)] p-5">
          <h3 className="m-0 font-display text-[18px] text-[var(--ink)]">{en ? "Feeds your daily plan" : "Polni tvoj dnevni plan"}</h3>
          <p className="mt-2 text-[14px] leading-[1.6] text-[var(--ink-soft)]">
            {en
              ? "Goals aren't a list you forget. The AI reads them every morning, weighs your tasks against them, and proposes a day that moves the year forward — automated goal tracking, daily alignment."
              : "Cilji niso seznam, ki ga pozabiš. AI jih bere vsako jutro, pretehta tvoje naloge in predlaga dan, ki premakne leto naprej — avtomatizirano sledenje ciljem, dnevna usklajenost."}
          </p>
        </div>
      </div>
    </section>
  );
}
